import { z } from 'zod';
/** Cnapse pre-printed label serial prefix (not an entity-type indicator; not ISO country CN). */
export const CNAPSE_LABEL_PREFIX = 'CNP-';
export const CNAPSE_LABEL_BARCODE_REGEX = /^CNP-[0-9A-Z]{12}$/;
export const cnapseLabelBarcodeSchema = z
    .string()
    .trim()
    .transform((s) => s.toUpperCase())
    .refine((s) => CNAPSE_LABEL_BARCODE_REGEX.test(s), {
    message: 'Barcode must match CNP- followed by 12 Base36 characters',
});
export const INVENTORY_LABEL_ENTITY_TYPE = {
    CONTAINER: 'container',
    CAGE: 'cage',
};
export const inventoryLabelEntityTypeSchema = z.enum([
    INVENTORY_LABEL_ENTITY_TYPE.CONTAINER,
    INVENTORY_LABEL_ENTITY_TYPE.CAGE,
]);
const DEFAULT_LABEL_URL_HOSTS = [
    'app.cnapse.ai',
    'wafertracker.local',
    'localhost',
    '127.0.0.1',
];
function isAllowedLabelUrlHost(hostname) {
    const host = hostname.toLowerCase();
    if (DEFAULT_LABEL_URL_HOSTS.includes(host)) {
        return true;
    }
    if (host.endsWith('.cnapse.ai') || host.endsWith('.wafertracker.local')) {
        return true;
    }
    return false;
}
/**
 * Strict parser: canonical CNP- token or null.
 */
export function parseInventoryLabelInput(raw) {
    const trimmed = raw.trim();
    if (!trimmed) {
        return null;
    }
    if (CNAPSE_LABEL_BARCODE_REGEX.test(trimmed.toUpperCase())) {
        return trimmed.toUpperCase();
    }
    const tryParseUrl = (input) => {
        try {
            const url = new URL(input);
            if (!isAllowedLabelUrlHost(url.hostname)) {
                return null;
            }
            const segments = url.pathname.split('/').filter(Boolean);
            const labelSegmentIndex = segments.findIndex((s) => s.toLowerCase() === 'l');
            if (labelSegmentIndex < 0 || labelSegmentIndex !== segments.length - 2) {
                return null;
            }
            const token = decodeURIComponent(segments[segments.length - 1]).toUpperCase();
            return CNAPSE_LABEL_BARCODE_REGEX.test(token) ? token : null;
        }
        catch {
            return null;
        }
    };
    if (trimmed.includes('://') || trimmed.startsWith('//')) {
        const normalized = trimmed.startsWith('//') ? `https:${trimmed}` : trimmed;
        return tryParseUrl(normalized);
    }
    if (trimmed.includes('/') || trimmed.toLowerCase().startsWith('www.')) {
        return tryParseUrl(`https://${trimmed}`);
    }
    return null;
}
export function buildInventoryLabelUrl(barcode, baseUrl) {
    const normalizedBase = baseUrl.replace(/\/$/, '');
    return `${normalizedBase}/l/${encodeURIComponent(barcode)}`;
}
export const InventoryLabelResolveQuerySchema = z.object({
    barcode: z.string().trim().min(1),
});
export const InventoryLabelAssignRequestSchema = z.object({
    barcode: cnapseLabelBarcodeSchema,
    entityType: z.literal(INVENTORY_LABEL_ENTITY_TYPE.CONTAINER),
    entityId: z.number().int().positive(),
    replaceExisting: z.boolean().optional().default(false),
});
export const InventoryLabelResolveResponseSchema = z.discriminatedUnion('status', [
    z.object({
        status: z.literal('linked'),
        entityType: inventoryLabelEntityTypeSchema,
        entityId: z.number().int().positive(),
        barcode: z.string(),
    }),
    z.object({
        status: z.literal('unassigned'),
        barcode: z.string(),
    }),
    z.object({
        status: z.literal('invalid'),
        barcode: z.string(),
    }),
]);
export const InventoryLabelResolveApiResponseSchema = z.object({
    success: z.literal(true),
    data: InventoryLabelResolveResponseSchema,
});
export const InventoryLabelAssignResponseSchema = z.object({
    success: z.literal(true),
    data: z.object({
        barcode: z.string(),
        entityType: z.literal(INVENTORY_LABEL_ENTITY_TYPE.CONTAINER),
        entityId: z.number().int().positive(),
    }),
});
export const LastBarcodeScanPayloadSchema = z.object({
    rawBarcode: z.string(),
    /** Canonical CNP- token when parse succeeds; null for parse_error scans. */
    barcode: z.string().nullable(),
    scannedAt: z.string().datetime(),
    scannerSerialNumber: z.string(),
    source: z.enum(['passive', 'command', 'manual', 'handheld']),
    efemClientId: z.number().int().positive().optional(),
    efemClientName: z.string().optional(),
    loadPortId: z.number().int().optional(),
    gatewaySerialNumber: z.string().optional(),
    resolutionStatus: z.string(),
});
export const LastBarcodeScanApiResponseSchema = z.object({
    success: z.literal(true),
    data: LastBarcodeScanPayloadSchema.nullable(),
});
//# sourceMappingURL=inventory-label.js.map